<template>
  <v-container>
    <v-row align="center" justify="center">
      <v-col cols="12" lg="8">
        <v-card>
          <v-toolbar>
            <v-btn icon @click="$router.go(-1)">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-toolbar-title>
              {{ $t('Game {0}', [id]) }}
            </v-toolbar-title>
            <v-spacer />
            <game-menu :id="id" />
            <preloader :active="!game"></preloader>
          </v-toolbar>
          <v-card-text>
            <v-text-field
              :value="game ? game.provably_fair_game.secret : ''"
              :label="$t('Server secret')"
              outlined
              readonly
              :disabled="!game"
              :hint="$t('Randomly generated by the server before each game.')"
              persistent-hint
              class="mb-5"
            />

            <v-text-field
              :value="game ? game.provably_fair_game.server_seed : ''"
              :label="$t('Server seed')"
              outlined
              readonly
              :disabled="!game"
              :hint="$t('Randomly generated by the server before each game.')"
              persistent-hint
              class="mb-5"
            />

            <v-text-field
              :value="game ? game.provably_fair_game.hash : ''"
              :label="$t('Server hash')"
              outlined
              readonly
              :disabled="!game"
              :hint="$t('HMAC SHA256 hash of the server secret with server seed as the key, revealed to player before each game.')"
              persistent-hint
              append-icon="mdi-code-tags"
              @click:append="serverHashModal = true"
              class="mb-5"
            />

            <v-text-field
              :value="game ? game.provably_fair_game.client_seed : ''"
              :label="$t('Client seed')"
              outlined
              readonly
              :disabled="!game"
              :hint="$t('A random string, which is provided before each game from the client side (by the player).')"
              persistent-hint
              class="mb-5"
            />

            <v-text-field
              :value="game ? game.provably_fair_game.client_hash : ''"
              :label="$t('Client hash')"
              outlined
              readonly
              :disabled="!game"
              :hint="$t('HMAC SHA256 hash of the server secret and server seed with client seed as the key.')"
              persistent-hint
              append-icon="mdi-code-tags"
              @click:append="clientHashModal = true"
              class="mb-5"
            />

            <v-text-field
              :value="game ? game.provably_fair_game.shift_value : ''"
              :label="$t('Shift value')"
              outlined
              readonly
              :disabled="!game"
              :hint="$t('Last 5 chars of the Client hash are converted to a decimal value.')"
              persistent-hint
              append-icon="mdi-code-tags"
              @click:append="shiftValueModal = true"
              class="mb-5"
            />

            <v-text-field
              :value="game ? game.gameable[game.gameable.secret_attribute] : ''"
              :label="$t('Adjusted secret')"
              outlined
              readonly
              :disabled="!game"
              :hint="game ? game.gameable.secret_attribute_hint : ''"
              persistent-hint
            />

            <v-dialog v-model="serverHashModal" width="600">
              <v-card>
                <v-toolbar>
                  <v-toolbar-title>
                    {{ $t('Server hash') }}
                  </v-toolbar-title>
                  <v-spacer />
                  <v-btn icon @click="serverHashModal = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text class="pa-4">
                  <p>{{ $t('Server hash can be calculated using the following PHP code.') }}</p>
                  <v-textarea
                    ref="server_hash"
                    outlined
                    readonly
                    :label="$t('Code')"
                    :value="serverHashCalculationCode"
                  />
                  <v-btn @click="copyToClipboard($refs.server_hash)">
                    {{ $t('Copy') }}
                  </v-btn>
                  <v-btn href="http://phpfiddle.org" target="_blank">
                    {{ $t('Run on phpfiddle.org') }}
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-dialog>

            <v-dialog v-model="clientHashModal" width="600">
              <v-card>
                <v-toolbar>
                  <v-toolbar-title>
                    {{ $t('Client hash') }}
                  </v-toolbar-title>
                  <v-spacer />
                  <v-btn icon @click="clientHashModal = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text class="pa-4">
                  <p>{{ $t('Client hash can be calculated using the following PHP code.') }}</p>
                  <v-textarea
                    ref="client_hash"
                    outlined
                    readonly
                    :label="$t('Code')"
                    :value="clientHashCalculationCode"
                  />
                  <v-btn @click="copyToClipboard($refs.client_hash)">
                    {{ $t('Copy') }}
                  </v-btn>
                  <v-btn href="http://phpfiddle.org" target="_blank">
                    {{ $t('Run on phpfiddle.org') }}
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-dialog>

            <v-dialog v-model="shiftValueModal" width="600">
              <v-card>
                <v-toolbar>
                  <v-toolbar-title>
                    {{ $t('Shift value') }}
                  </v-toolbar-title>
                  <v-spacer />
                  <v-btn icon @click="shiftValueModal = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text class="pa-4">
                  <p>{{ $t('Server hash can be calculated using the following PHP code.') }}</p>
                  <v-textarea
                    ref="shift_value"
                    outlined
                    readonly
                    :label="$t('Code')"
                    :value="shiftValueCalculationCode"
                  />
                  <v-btn @click="copyToClipboard($refs.shift_value)">
                    {{ $t('Copy') }}
                  </v-btn>
                  <v-btn href="http://phpfiddle.org" target="_blank">
                    {{ $t('Run on phpfiddle.org') }}
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-dialog>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios'
import GameMenu from '~/components/GameMenu'
import { copyToClipboard } from '~/plugins/utils'
import Preloader from '~/components/Preloader'

export default {
  middleware: ['auth', 'verified', '2fa_passed'],

  components: { Preloader, GameMenu },

  props: ['id'],

  metaInfo () {
    return { title: this.$t('Game {0}', [this.id]) }
  },

  data () {
    return {
      game: null,
      serverHashModal: false,
      clientHashModal: false,
      shiftValueModal: false
    }
  },

  computed: {
    serverHashCalculationCode () {
      return this.game
        ? `print hash_hmac("sha256", "${this.game.provably_fair_game.secret}", "${this.game.provably_fair_game.server_seed}")`
        : ''
    },
    clientHashCalculationCode () {
      return this.game
        ? `print hash_hmac("sha256", "${this.game.provably_fair_game.secret}${this.game.provably_fair_game.server_seed}", "${this.game.provably_fair_game.client_seed}")`
        : ''
    },
    shiftValueCalculationCode () {
      return this.game
        ? `print hexdec(substr("${this.game.provably_fair_game.client_hash}", -5))`
        : ''
    }
  },

  async created () {
    const { data: { game } } = await axios.get(`/api/history/games/${this.id}/verify`)

    if (!game.gameable.is_provably_fair) {
      this.$store.dispatch('message/error', { text: this.$t('This type of game is not provably fair.') })
      this.$router.back()
    }

    this.game = game
  },

  methods: {
    copyToClipboard (ref) {
      return copyToClipboard(ref.$el.querySelector('textarea'))
    }
  }
}
</script>
